import { WrappedPair } from '@/entities/WrappedPair';
import { usePositionListByUser } from '@/features/account/positionHook';
import { useTradeStore } from '@/features/trade/store';
import React from 'react';
import { useChainId, useUserAddr } from '../web3/useChain';

interface UseFilteredPositionsParams {
  currentPair?: WrappedPair;
  isTradePage?: boolean;
}

/**
 * Custom hook to fetch and filter positions based on current pair and trade page context
 * @param chainId - Chain ID for data fetching
 * @param userAddr - User address for data fetching
 * @param isTradePage - Whether this is being used on a trade page
 * @returns Object containing filtered position list, current pair, and all positions
 */
export const useFilteredPositions = ({ currentPair, isTradePage = true }: UseFilteredPositionsParams) => {
  const { hideOtherPair } = useTradeStore();
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const allPositions = usePositionListByUser(chainId, userAddr);

  const positionList = React.useMemo(() => {
    if (!hideOtherPair || !isTradePage) return allPositions;
    if (!allPositions || !currentPair) return [];
    return allPositions.filter((position) => position.rootPair.id === currentPair.id);
  }, [allPositions, currentPair, hideOtherPair, isTradePage]);

  return positionList;
};
